import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, Lock, Printer } from 'lucide-react';
import type { PedidoDetalle, PedidoItemVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { SpinnerInline } from '@renderer/components/ui/SpinnerInline';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { CorregirFormaPagoModal } from '@renderer/components/cobro/CorregirFormaPagoModal';
import { CobrarDiferenciaModal } from '@renderer/components/cobro/CobrarDiferenciaModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { puedeUsarCanal } from '@shared/permisos';
import { IPC_CHANNELS } from '@shared/constants';

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  onClose: () => void;
  onItemCancelado: () => void;
}

/**
 * Muestra los items del pedido. Permite cancelar items individuales con
 * motivo + PIN del dueño (auditoría antifraude — Sprint 5 RN-S5-03).
 * Sólo activos pueden cancelarse y sólo si el pedido NO está pagado/cancelado.
 *
 * ---
 *
 * ## Sprint 115 — Y ahora, cancelar el pedido entero (DT-101)
 *
 * `pedidos.cancelar()` existía completo desde el Sprint 8 —revierte inventario, libera la mesa,
 * exige PIN si va en ruta— y **sus dos únicas puertas eran "Liberar mesa" y el tablero de
 * domicilios**. Un pedido de mostrador no tenía por dónde cancelarse, y uno de mesa sólo de
 * refilón, al liberar la mesa.
 *
 * Lo que dejaba: se captura algo por error, la única herramienta es cancelar **renglón por
 * renglón**, el pedido queda en $0 y sigue vivo. El Sprint 113 le quitó el poder de frenar el
 * cierre, pero el fantasma seguía ahí — el tablero lo contaba en "En cocina" porque cuenta por
 * `estado`.
 *
 * Con el pedido en `cancelado`, **todas las pantallas lo excluyen solas**: no hubo que parchear
 * un contador por pantalla.
 */
type AccionCancelar =
  | { tipo: 'item'; item: PedidoItemVista }
  | { tipo: 'pedido' };

export function DetallePedidoModal({ abierto, pedidoId, onClose, onItemCancelado }: Props) {
  const [detalle, setDetalle] = useState<PedidoDetalle | null>(null);
  const [cargando, setCargando] = useState(false);
  const [accion, setAccion] = useState<AccionCancelar | null>(null);
  const [motivo, setMotivo] = useState('');
  const [pin, setPin] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const negocio = useNegocioStore((s) => s.negocio);
  const sesion = useSessionStore((s) => s.sesion);
  const ajustes = useAjustesPermisos((s) => s.ajustes);
  const navigate = useNavigate();
  /** Sprint 139 — Corregir la forma de pago de un cobro ya registrado. */
  const [corrigiendoPago, setCorrigiendoPago] = useState(false);
  /** Sprint 141 — Cobrar lo que falta tras agregarle productos a un pedido ya cobrado. */
  const [cobrandoDiferencia, setCobrandoDiferencia] = useState(false);
  /**
   * Sprint 145 — El comprobante después de cobrar la diferencia.
   *
   * Lo preguntó el dueño probándolo: *"doy clic en COBRAR… ¿y luego qué pasa? Después de dar clic
   * ya no se ve qué más pasa"*. Y no pasaba nada: se registraba el dinero y la pantalla volvía al
   * detalle. El cliente se quedaba con un papel que ya no dice ni lo que se llevó ni lo que pagó.
   *
   * El cobro normal sí abre el ticket al terminar. Éste ahora también.
   */
  const [ticketId, setTicketId] = useState<number | null>(null);
  /** Sprint 139 — Las correcciones que ya tuvo este pedido. Vacío = ninguna (o no hay permiso). */
  const [correcciones, setCorrecciones] = useState<
    Array<{ id: number; motivo: string; usuario: string | null; pagosAntes: string; pagosDespues: string; fecha: string }>
  >([]);

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const cargar = async () => {
    if (!pedidoId) return;
    setCargando(true);
    const res = await window.api.pedidos.obtener(pedidoId);
    if (res.ok) setDetalle(res.data);
    /*
      Sprint 139 — El historial de correcciones. Es sólo del dueño, así que para los demás
      oficios el canal responde que no y la lista queda vacía: no es un error que haya que
      enseñar, es que esa información no es suya.
    */
    const corr = await window.api.pedidos.correccionesPago(pedidoId);
    setCorrecciones(corr.ok ? corr.data : []);
    setCargando(false);
  };

  useEffect(() => {
    if (!abierto || !pedidoId) {
      setDetalle(null);
      setAccion(null);
      setMotivo('');
      setPin('');
      setError(null);
      return;
    }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, pedidoId]);

  if (!abierto) return null;

  /**
   * Sprint 145 — Una venta cobrada **sí** se puede corregir; una cancelada no.
   *
   * `'pagado'` estaba aquí, y con él la dueña no podía arreglar su propio error de captura: el
   * sistema la mandaba a escribirlo en observaciones. Lo que protege el dinero no es prohibir la
   * corrección —sigue pidiendo PIN y motivo— sino que el saldo quede a la vista, que es lo que
   * hace el aviso de abajo.
   */
  const pedidoBloqueado = detalle?.estado === 'cancelado';

  /**
   * Sprint 139 — Quién ve el botón de corregir la forma de pago.
   *
   * Se pregunta por el **canal**, no por el rol: así sigue a la matriz cuando el negocio mueva el
   * interruptor de *"la caja puede cancelar"*, en vez de desincronizarse en silencio. El servidor
   * vuelve a decidirlo de todas formas — esconder un botón no es control de acceso.
   */
  /**
   * Sprint 140 — *"Agréguele otra cosa, que me llegó visita."*
   *
   * La frontera la decidió el negocio y es **si ya salió**: mientras la moto no se haya ido o el
   * cliente no se haya llevado su bolsa.
   *
   * Sprint 141 — Y ya no importa si pagó: al que pagó también se le puede agregar, y lo que
   * falte se cobra después (arriba aparece cuánto). `entregado` sigue valiendo en mesa —el plato
   * servido, con la gente ahí— y no en mostrador, donde significa que se lo llevó.
   *
   * El servidor vuelve a comprobarlo todo: esconder un botón nunca fue un control.
   */
  const puedeAgregarProductos =
    detalle !== null &&
    !['cancelado', 'en_ruta', 'no_entregado'].includes(detalle.estado) &&
    (detalle.estado !== 'entregado' || detalle.mesaId != null);

  const puedeCorregirPago =
    detalle !== null &&
    detalle.pagos.length > 0 &&
    detalle.estado !== 'cancelado' &&
    sesion !== null &&
    puedeUsarCanal(sesion.rol, IPC_CHANNELS.CORRECCION_PAGO_REGISTRAR, ajustes);

  /*
    Sprint 148 — Reimprimir la nota. Se ofrece a quien puede pedirle el papel al servidor; quien
    no, no vería más que un botón que rebota. El permiso de verdad sigue viviendo en el canal:
    esconderlo aquí es cortesía, no control de acceso.
  */
  const puedeImprimirNota =
    detalle !== null &&
    sesion !== null &&
    puedeUsarCanal(sesion.rol, IPC_CHANNELS.TICKET_HTML, ajustes);

  /*
    Sprint 141 — El botón de cobrar la diferencia sigue la misma regla que su hermano.

    El aviso de lo que falta se ve siempre —que el dinero esté pendiente es un hecho, y
    esconderlo es peor—, pero la acción sólo se ofrece a quien el servidor va a dejar hacerla.
    Un botón que rebota enseña a desconfiar de la pantalla.
  */
  const puedeCobrarDiferencia =
    sesion !== null &&
    puedeUsarCanal(sesion.rol, IPC_CHANNELS.PEDIDOS_COBRAR_DIFERENCIA, ajustes);

  const handleCancelar = async () => {
    if (!accion || !sesion) return;
    if (motivo.trim().length < 10) {
      setError('El motivo debe tener al menos 10 caracteres.');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError('PIN inválido. Debe ser de 4 dígitos.');
      return;
    }
    setEnviando(true);
    setError(null);
    const res =
      accion.tipo === 'item'
        ? await window.api.pedidos.cancelarItem({
            itemId: accion.item.id,
            motivo: motivo.trim(),
            usuarioId: sesion.usuarioId,
            supervisorPin: pin,
          })
        : await window.api.pedidos.cancelar(pedidoId!, motivo.trim(), sesion.usuarioId, pin);
    setEnviando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    setAccion(null);
    setMotivo('');
    setPin('');
    // Cancelado el pedido entero no queda nada que mirar aquí: se cierra y la lista se refresca.
    if (accion.tipo === 'pedido') {
      onItemCancelado();
      onClose();
      return;
    }
    await cargar();
    onItemCancelado();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">Detalle del pedido</h2>
            {detalle && (
              <p className="text-xs text-slate-500">
                #{String(detalle.numeroPedido).padStart(5, '0')} · estado: {detalle.estado}
              </p>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {cargando ? (
            <SpinnerInline texto="Cargando pedido…" className="py-8" />
          ) : !detalle ? (
            <div className="text-center text-slate-400 py-8">Pedido no disponible.</div>
          ) : null}

          {detalle && !cargando && (detalle.mesaIds || detalle.pedidoPadreId) && (
            <div className="space-y-1">
              {detalle.mesaIds && detalle.mesaIds.length > 0 && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
                  Mesas asignadas: {detalle.mesaIds.join(', ')}
                </div>
              )}
              {detalle.pedidoPadreId && (
                <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-800">
                  Sub-pedido derivado de #{String(detalle.pedidoPadreId).padStart(5, '0')}
                </div>
              )}
            </div>
          )}

          {detalle && !cargando && (accion ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  {accion.tipo === 'item' ? (
                    <>
                      Vas a cancelar:{' '}
                      <strong>
                        {accion.item.cantidad}× {accion.item.productoNombre ?? accion.item.paqueteNombre}
                      </strong>
                      .{' '}
                    </>
                  ) : (
                    <>
                      Vas a cancelar <strong>el pedido completo</strong> —
                      {' '}{detalle.items.filter((i) => i.estado === 'activo').length} renglón(es) por{' '}
                      <strong>{formatPrecio(detalle.total, monedaSimbolo, locale)}</strong>. Lo que
                      hayas descontado del almacén <strong>regresa a existencias</strong>
                      {detalle.mesaIds && detalle.mesaIds.length > 0 ? ' y la mesa queda libre' : ''}.{' '}
                    </>
                  )}
                  Esta acción queda registrada en auditoría con tu usuario, el PIN del supervisor y
                  el motivo. No se puede revertir.
                </div>
              </div>

              <div>
                <label className="label" htmlFor="motivoCancelar">
                  Motivo de cancelación (obligatorio, 10..500 caracteres)
                </label>
                <textarea
                  id="motivoCancelar"
                  className="input min-h-[80px]"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  maxLength={500}
                  placeholder={
                    accion.tipo === 'item'
                      ? 'Ej: el cliente cambió de opinión antes de servirse'
                      : 'Ej: se capturó por error / el cliente ya no lo quiso'
                  }
                  disabled={enviando}
                />
                <div className="text-xs text-slate-500 mt-1">{motivo.length}/500</div>
              </div>

              <div>
                <label className="label flex items-center gap-1" htmlFor="pinCancel">
                  <Lock className="h-4 w-4" /> PIN del dueño (4 dígitos)
                </label>
                <input
                  id="pinCancel"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  className="input text-center text-2xl tracking-widest font-mono"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  autoFocus
                  disabled={enviando}
                />
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setAccion(null);
                    setMotivo('');
                    setPin('');
                    setError(null);
                  }}
                  className="btn-ghost flex-1"
                  disabled={enviando}
                >
                  Atrás
                </button>
                <button
                  onClick={handleCancelar}
                  className="btn-danger flex-1"
                  disabled={enviando || motivo.trim().length < 10 || pin.length !== 4}
                >
                  {enviando
                    ? 'Procesando...'
                    : accion.tipo === 'item'
                      ? 'Cancelar item'
                      : 'Cancelar el pedido'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/*
                Sprint 141 — Lo que falta por cobrar, **antes que nada**.

                Aparece cuando se le agregó algo a un pedido ya cobrado. Va arriba y en ámbar
                porque es dinero que el negocio todavía no tiene y que se olvida con una sola
                distracción: el cliente se va con la bolsa y la cuenta queda corta.
              */}
              {(detalle.faltaCobrar ?? 0) > 0 && (
                <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-amber-900">
                      {detalle.pagaAlEntregar ? 'Lo cobra el repartidor' : 'Falta cobrar'}
                    </span>
                    <span className="font-mono text-lg font-bold text-amber-900">
                      {formatPrecio(detalle.faltaCobrar ?? 0, monedaSimbolo, locale)}
                    </span>
                  </div>
                  {/*
                    Sprint 141 — El acuse del acuerdo, que faltaba.

                    Probado en local: al pulsar "que la cobre el repartidor" el servidor marcaba el
                    acuerdo correctamente… y esta caja quedaba **idéntica**. Quien lo hizo no podía
                    saber si su toque había servido, y el siguiente que abría el pedido volvía a
                    intentar cobrarlo. Un acuerdo que no se ve no es un acuerdo: es un botón mudo.
                  */}
                  {detalle.pagaAlEntregar ? (
                    <p className="mt-1 text-xs text-amber-800">
                      Se acordó cobrarlo al entregar
                      {detalle.pagaAlEntregarPorNombre
                        ? ` · lo autorizó ${detalle.pagaAlEntregarPorNombre}`
                        : ''}
                      . Va anotado en la ficha que se lleva la moto.
                    </p>
                  ) : (
                    /*
                      Sprint 146 — Corregir no es agregar, y decirlo mal confunde justo a quien
                      acaba de corregir: tras cambiar medio litro por un litro la pantalla decía
                      "se le agregaron productos", que no fue lo que pasó. Si hay algún renglón
                      cancelado, la frase es la de la corrección; si no, la de siempre.
                    */
                    <p className="mt-1 text-sm text-amber-900">
                      {detalle.items.some((i) => i.estado === 'cancelado')
                        ? 'La cuenta cambió después de cobrarle: esto es lo que falta, no el total.'
                        : 'Se le agregaron productos después de cobrarle.'}
                    </p>
                  )}
                  {puedeCobrarDiferencia && (
                    <button
                      type="button"
                      onClick={() => setCobrandoDiferencia(true)}
                      className={`mt-2 min-h-[44px] w-full ${
                        detalle.pagaAlEntregar ? 'btn-ghost border border-amber-300' : 'btn-primary'
                      }`}
                    >
                      {detalle.pagaAlEntregar ? 'Cobrarlo ahora' : 'Cobrar la diferencia'}
                    </button>
                  )}
                </div>
              )}

              {pedidoBloqueado && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2 text-sm text-slate-600">
                  Este pedido está cancelado. No se puede modificar.
                </div>
              )}

              {/*
                Sprint 145 — El dinero que quedó a favor del cliente tras corregir.

                Si al quitar un renglón la cuenta baja de lo que ya se cobró, ese dinero es del
                cliente. **No se mueve solo**: sacar efectivo de la caja es una decisión de quien
                está ahí, no de un recálculo. Pero se dice, en grande y con el monto, porque el
                único modo de que no se olvide es que esté escrito.

                Sprint 146 — Se nombran las **dos** salidas, porque el dueño las nombró así:
                *"ese dinero es una nota de crédito: o se le devuelve, o si se agrega otro
                producto sólo se cobra la diferencia"*. Antes el aviso sólo ofrecía devolver
                efectivo, y quien leyera eso con el cliente enfrente pidiendo otra cosa sacaría
                billetes para volver a cobrarlos — dos movimientos de caja donde no hacía falta
                ninguno.
              */}
              {(detalle.montoPorDevolver ?? 0) > 0 && (
                <div className="rounded-lg border-2 border-violet-300 bg-violet-50 px-4 py-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-violet-900">
                      A favor del cliente
                    </span>
                    <span className="font-mono text-lg font-bold text-violet-900">
                      {formatPrecio(detalle.montoPorDevolver ?? 0, monedaSimbolo, locale)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-violet-900">
                    Pagó de más porque la cuenta bajó. Devuélveselo, o agrégale lo que quiera
                    llevarse: se descuenta solo de ese saldo y únicamente le cobras la diferencia.
                  </p>
                </div>
              )}

              {/*
                Sprint 140 — *"Agréguele un medio kilo de costilla, que me llegó visita."*

                Va arriba, junto a los productos, porque es lo que se está mirando cuando el
                cliente llama. Lleva al mismo punto de venta de siempre, en modo agregar: los
                productos nuevos se suman a esta cuenta y la cocina recibe una comanda **sólo con
                lo nuevo**, que dice "no reprepares el resto".
              */}
              {puedeAgregarProductos && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`/pos?agregar=${detalle.id}`);
                  }}
                  className="btn-ghost min-h-[44px] w-full border border-dashed border-slate-300 text-sm"
                >
                  ＋ Agregar productos a este pedido
                </button>
              )}

              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {detalle.items.map((it) => {
                  const cancelado = it.estado === 'cancelado';
                  return (
                    <li
                      key={it.id}
                      className={`px-4 py-3 flex items-center justify-between ${
                        cancelado ? 'bg-red-50' : ''
                      }`}
                    >
                      <div className="flex-1">
                        <div className={`font-medium ${cancelado ? 'line-through text-slate-400' : ''}`}>
                          {it.cantidad}× {it.productoNombre ?? it.paqueteNombre}
                        </div>
                        {it.notasEspeciales && (
                          <div className="text-xs text-slate-500 italic">{it.notasEspeciales}</div>
                        )}
                        {cancelado && it.motivoCancelacion && (
                          <div className="text-xs text-red-700 mt-1">
                            ✕ Cancelado: {it.motivoCancelacion}
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-3">
                        <div
                          className={`font-mono font-bold ${cancelado ? 'line-through text-slate-400' : ''}`}
                        >
                          {formatPrecio(it.subtotal, monedaSimbolo, locale)}
                        </div>
                        {!cancelado && !pedidoBloqueado && (
                          <button
                            onClick={() => {
                              setAccion({ tipo: 'item', item: it });
                              setError(null);
                            }}
                            className="btn-ghost text-xs text-red-600 mt-1"
                          >
                            Cancelar item
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-mono">{formatPrecio(detalle.subtotal, monedaSimbolo, locale)}</span>
                </div>
                {detalle.impuesto > 0 && (
                  <div className="flex justify-between">
                    <span>Impuesto</span>
                    <span className="font-mono">{formatPrecio(detalle.impuesto, monedaSimbolo, locale)}</span>
                  </div>
                )}
                {detalle.costoEnvio > 0 && (
                  <div className="flex justify-between text-xs">
                    <span>
                      Envío {detalle.envioPagaCliente ? '(paga al recibir)' : '(en total)'}
                    </span>
                    <span className="font-mono">
                      {formatPrecio(detalle.costoEnvio, monedaSimbolo, locale)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base pt-1 border-t border-slate-200">
                  <span>Total</span>
                  <span className="font-mono">{formatPrecio(detalle.total, monedaSimbolo, locale)}</span>
                </div>
                {detalle.propinaTotal > 0 && (
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>+ Propina</span>
                    <span className="font-mono">
                      {formatPrecio(detalle.propinaTotal, monedaSimbolo, locale)}
                    </span>
                  </div>
                )}
              </div>

              {detalle.pagos.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Pagos registrados</div>
                  <ul className="text-xs space-y-1">
                    {detalle.pagos.map((pg) => (
                      <li key={pg.id} className="flex justify-between">
                        <span>
                          {pg.metodo}
                          {/*
                            Sprint 141 — La huella de auditoría no se vuelca cruda a la pantalla.

                            Esta línea imprimía tal cual el sello que el sistema guarda para poder
                            reconstruir quién dio por bueno un depósito:
                            `VERIFICADO_EN_CUENTA:9:2026-09-19T19:49:05.303Z`. En la base está
                            bien —es la huella—; en la pantalla de quien cobra es ruido que nadie
                            puede leer, justo debajo de los importes que sí hay que leer.
                          */}
                          {pg.referencia ? ` (${referenciaLegible(pg.referencia, locale)})` : ''}
                          {pg.propina > 0 && ` + propina ${formatPrecio(pg.propina, monedaSimbolo, locale)}`}
                        </span>
                        <span className="font-mono">{formatPrecio(pg.monto, monedaSimbolo, locale)}</span>
                      </li>
                    ))}
                  </ul>

                  {/*
                    Sprint 139 — *"Dijo efectivo y pagó con tarjeta."*

                    Vive aquí, pegado a los pagos, porque es donde se mira cuando surge el
                    problema: el cliente enfrente, el ticket impreso y la caja diciendo algo que
                    no pasó. El servidor decide de verdad (el canal rechaza días y cortes
                    cerrados, y propinas ya repartidas); esconder el botón no sería control de
                    acceso.
                  */}
                  {/*
                    Sprint 139 (H-2 de la auditoría) — **El acta se lee aquí.**

                    Sin lector, el acta es teatro: el canal existía, estaba tipado y probado, y
                    ninguna pantalla lo invocaba — el mismo hallazgo del Sprint 137 repetido. Es
                    lo que convierte la corrección de "auditable" en "indetectable".
                  */}
                  {correcciones.length > 0 && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
                      <div className="text-xs font-semibold uppercase text-amber-900">
                        Se corrigió la forma de pago
                      </div>
                      <ul className="mt-1 space-y-1 text-xs text-amber-900">
                        {correcciones.map((c) => (
                          <li key={c.id}>
                            <b>{resumenPagos(c.pagosAntes)} → {resumenPagos(c.pagosDespues)}</b>
                            <br />
                            {new Date(c.fecha).toLocaleString(locale)} · {c.usuario ?? 'sin nombre'}{' '}
                            · {c.motivo}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {puedeCorregirPago && (
                    <button
                      type="button"
                      onClick={() => setCorrigiendoPago(true)}
                      className="btn-ghost mt-2 min-h-[44px] w-full text-sm"
                    >
                      Corregir forma de pago
                    </button>
                  )}

                </div>
              )}
            </>
          ))}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex items-center justify-between gap-3">
          {/*
            Sprint 115 — La salida que faltaba, y por qué vive AQUÍ y en discreto.

            Es la acción más destructiva del modal, así que no compite con los renglones: va al pie,
            en texto y no en botón rojo grande, para que se llegue queriendo. Pero **existe y se
            ve**, que es justo lo que no pasaba: hasta hoy la única forma de deshacer un pedido
            capturado por error era cancelar renglón por renglón, y aun así quedaba vivo.

            Sólo aparece si el pedido admite cancelación. El servidor decide de verdad
            (`pedidos:cancelar` rechaza pagados y exige PIN); esconder un botón no es control de
            acceso.
          */}
          {detalle && !accion && !pedidoBloqueado ? (
            <button
              onClick={() => {
                setAccion({ tipo: 'pedido' });
                setError(null);
              }}
              className="btn-ghost text-sm text-red-600 hover:bg-red-50"
            >
              Cancelar todo el pedido
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {/*
              Sprint 148 — *"¿Se puede reimprimir una nota? ¿Y en qué apartado?"*

              La dueña, mientras atendía. Reimprimir **ya funcionaba**: el papel se arma con lo
              guardado y desde el Sprint 129 la segunda emisión sale marcada `*** COPIA ***`, para
              que dos papeles con el mismo folio no puedan cobrarse dos veces. Lo que no existía
              era la puerta: al botón sólo se llegaba en el instante de cobrar, y pasado ese
              instante la función estaba viva y era inalcanzable.

              Dice **nota**, que es la palabra que usó ella, y vive en el pie del detalle: el
              mismo sitio se abre desde Ventas y desde Pedidos, así que la reimpresión queda donde
              está la venta, sin una pantalla nueva que aprender.
            */}
            {detalle && !pedidoBloqueado && puedeImprimirNota && (
              <button
                onClick={() => setTicketId(detalle.id)}
                className="btn-ghost flex min-h-[44px] items-center gap-2 text-sm"
              >
                <Printer className="h-4 w-4" aria-hidden="true" />
                Imprimir nota
              </button>
            )}
            <button onClick={onClose} className="btn-ghost min-h-[44px]">
              Cerrar
            </button>
          </div>
        </footer>
      </div>

      {detalle && (
        <CorregirFormaPagoModal
          abierto={corrigiendoPago}
          pedidoId={detalle.id}
          pagosActuales={detalle.pagos}
          onCerrar={() => setCorrigiendoPago(false)}
          // Se recarga el detalle: el ticket y los pagos que se ven aquí ya son otros.
          onCorregido={() => void cargar()}
        />
      )}

      {detalle && (
        <CobrarDiferenciaModal
          abierto={cobrandoDiferencia}
          pedidoId={detalle.id}
          falta={detalle.faltaCobrar ?? 0}
          /*
            Cobrar en la puerta sólo tiene sentido si es un domicilio que todavía no sale. El
            servidor lo vuelve a comprobar: esto sólo decide si se ofrece el botón.
          */
          puedeCobrarAlEntregar={
            detalle.tipo === 'delivery' &&
            // Sprint 141 — Si el acuerdo ya está tomado, ofrecerlo otra vez sólo confunde.
            !detalle.pagaAlEntregar &&
            !['en_ruta', 'entregado', 'no_entregado'].includes(detalle.estado)
          }
          onCerrar={() => setCobrandoDiferencia(false)}
          onCobrado={() => {
            void cargar();
            /*
              Y la lista de atrás, que si no se queda enseñando el "falta cobrar" de hace un
              segundo. Un número viejo en la pantalla del dinero se lee como un número.
            */
            onItemCancelado();
            /*
              El ticket **completo**, no un papel suelto con la diferencia. El cliente no tiene por
              qué guardar dos comprobantes y sumarlos: el documento que sirve para cualquier
              aclaración es el que refleja la venta final, y ahí se ven los dos pagos por separado.
            */
            setTicketId(detalle?.id ?? null);
          }}
        />
      )}

      {/* Sprint 145 — El comprobante actualizado, en cuanto entra la diferencia. */}
      <TicketPreviewModal
        abierto={ticketId !== null}
        pedidoId={ticketId}
        onClose={() => {
          setTicketId(null);
          void cargar();
        }}
      />
    </div>
  );
}

/**
 * Sprint 139 — El acta, en palabras de mostrador: "efectivo $450 → tarjeta $450".
 *
 * El JSON guarda más de lo que se enseña (referencia, recibido, fecha del pago original) porque
 * eso es lo que necesita quien audita; aquí va lo que necesita quien mira el pedido.
 */
function resumenPagos(json: string): string {
  try {
    const pagos = JSON.parse(json) as Array<{ metodo: string; monto: number }>;
    return pagos.map((p) => `${p.metodo} $${(p.monto / 100).toFixed(2)}`).join(' + ');
  } catch {
    return '—';
  }
}

/**
 * Sprint 141 — La referencia del pago, dicha para quien está en la caja.
 *
 * Cuando alguien da por bueno un depósito mirando su banco, el sistema guarda el sello
 * `VERIFICADO_EN_CUENTA:<usuario>:<fecha ISO>` para poder reconstruir después quién lo autorizó.
 * Esa cadena es correcta en la base y **ilegible en la pantalla**: se estaba imprimiendo entera
 * al lado del importe, en la única pantalla donde se revisa cómo entró el dinero.
 *
 * Las referencias que teclea una persona —los últimos 4 dígitos, el folio del banco— salen tal
 * cual: ésas sí las escribió alguien para leerlas.
 */
function referenciaLegible(referencia: string, locale: string): string {
  if (!referencia.startsWith('VERIFICADO_EN_CUENTA:')) return referencia;
  const iso = referencia.split(':').slice(2).join(':');
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return 'verificado en cuenta';
  return `verificado en cuenta · ${fecha.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}
